/**
 * tutorApplicationQuestions — les questions écrites de la candidature, leurs règles et ce qui part
 * au serveur, en pur.
 *
 * Port littéral des `formData` de l'ancien code, passés par `FormStepper` / `FormStep` :
 *  - le profil (étape 1) : `tutor/application/personalInfos/ApplicationPersonalInfos.js` ;
 *  - la motivation et la pédagogie écrites (étapes 2 et 4) : `motivation/InterviewScreen.js`
 *    (`motivationQuestions`, `pedagogyQuestions`, la précision de `PrecisionAnswerComponent.js`).
 *
 * Mêmes libellés, mêmes valeurs, même masquage (`is_masked`), mêmes règles de validation
 * (`data/utility/validation.js`, dans l'ordre où `react-hook-form` les lit : requis, minimum et
 * maximum, motif, puis la règle libre), même règle du bouton du pied (`hideNextButton` de
 * `FormStepper`), et le même corps de requête (`onSubmit`).
 */
import { subjectPriorities } from "../../../../../data/constants/formConstants"
import { numberRegExp, phoneRegExp } from "../../../../../data/utility/validation"
import { type UITutorLanguage, type UITutorLanguageLevel } from "../../../ui/types/tutorCandidature"
import { REQUIRED_MESSAGE } from "../../student/Account/studentSettingsAdapter"
import { LANGUAGE_LEVEL_MISSING } from "../Account/tutorProfileAdapter"

// ─── Les règles (validation.js) ───────────────────────────────────────────────────────────────

export { REQUIRED_MESSAGE }
export const LANGUAGE_LEVEL_MESSAGE = LANGUAGE_LEVEL_MISSING

export type FieldRules = {
    required?: boolean
    phone?: boolean
    number?: boolean
    min?: number
    max?: number
    noLeadingWhitespace?: boolean
}

/** Le message d'un champ, ou `null` : l'ordre de `react-hook-form` (requis, min/max, motif, libre). */
export function fieldError(value: string | null | undefined, rules: FieldRules): string | null {
    const text = value ?? ""
    if (rules.required && text === "") return REQUIRED_MESSAGE
    if (text === "") return null
    // `react-hook-form` ne compare min et max que pour une valeur lisible en nombre (« 12,5 » passe).
    const asNumber = +text
    if (!Number.isNaN(asNumber)) {
        if (rules.min != null && asNumber < rules.min) return `La valeur minimum est de ${rules.min}`
        if (rules.max != null && asNumber > rules.max) return `La valeur maximum est de ${rules.max}`
    }
    // `phoneRegExp` et `numberRegExp` de `validation.js` (« 0 » n'y est pas un nombre).
    if (rules.phone && !phoneRegExp.test(text)) return "Le numéro de téléphone est invalide"
    if (rules.number && !numberRegExp.test(text)) return "Nombre invalide"
    if (rules.noLeadingWhitespace && text[0] === " ") return "Le champ ne peut pas commencer par un espace"
    return null
}

// ─── Les questions ────────────────────────────────────────────────────────────────────────────

export type ApplicationOption = { value: string | number; label: string; detail?: string }

export type ApplicationInput = {
    name: string
    placeholder: string
    label?: string
    keyboard?: "default" | "phone-pad" | "decimal-pad"
    rules: FieldRules
    /** Le champ SIRET, masqué tant que « J'ai déjà le statut auto-entrepreneur » est éteint. */
    hiddenUnless?: "autoEntrepreneur"
}

export type ApplicationToggleKey = "abroadBac" | "autoEntrepreneur"

export type ApplicationQuestion =
    | {
          kind: "single"
          name: string
          label: string
          options: ApplicationOption[]
          layout?: "cards" | "chips"
          toggle?: ApplicationToggleKey
          /** Un champion pose la précision libre sous le choix (« Pourquoi ? ») : le bouton « Suivant ». */
          precision?: { placeholder: string }
          /** Masquée selon les réponses (`is_masked`). */
          masked?: (values: ApplicationValues) => boolean
      }
    | {
          kind: "multiple"
          name: string
          label: string
          options: ApplicationOption[]
          /** Au plus N choix, les autres refusés (`createCompetencyOptions`), et N au moins pour continuer. */
          exactly?: number
          masked?: (values: ApplicationValues) => boolean
      }
    | {
          kind: "text"
          label: string
          inputs: ApplicationInput[]
          /** La règle `required` au niveau de la question : le bouton n'apparaît qu'une fois tout écrit. */
          required?: boolean
          toggle?: ApplicationToggleKey
          note?: string
      }
    | { kind: "languages"; name: "foreign_languages"; label: string }

export type ApplicationLanguageAnswer = { id: number | string; proficiency?: number | null }

export type ApplicationValues = Record<string, unknown>

/** `formStepName` : le nom de l'étape, ou ceux de ses champs, que la mesure porte en propriété. */
export function questionStepName(question: ApplicationQuestion): string {
    if (question.kind === "text") return question.inputs.map((input) => input.name).join(",")
    return question.name
}

/** Les questions qui restent, une fois le masquage appliqué. */
export function visibleQuestions(questions: ApplicationQuestion[], values: ApplicationValues): ApplicationQuestion[] {
    return questions.filter((question) => !("masked" in question && question.masked?.(values)))
}

export type ApplicationSchoolSubject = {
    id: number | string
    name?: string | null
    is_specialty?: boolean | number | null
    category?: string | null
}

/** Les années du bac, de l'année en cours à 1960. */
export function graduationYears(currentYear: number): ApplicationOption[] {
    const years: ApplicationOption[] = []
    for (let year = currentYear; year >= 1960; year--) years.push({ value: year, label: String(year) })
    return years
}

const values = (labels: string[]): ApplicationOption[] => labels.map((label) => ({ value: label, label }))

const SIRET_NOTE =
    "*Les tuteurs doivent obligatoirement avoir le statut auto-entrepreneur pour exercer leur activité de tutorat. Si tu n'as pas encore ce statut, tu devras l'obtenir avant de commencer tes cours."

export const APPLICATION_TOGGLE_LABELS: Record<ApplicationToggleKey, string> = {
    abroadBac: "J'ai un équivalent du baccalauréat passé à l'étranger",
    autoEntrepreneur: "J'ai déjà le statut auto-entrepreneur",
}

/** Le profil, étape 1 (`ApplicationPersonalInfos.formData`). */
export function profileQuestions(args: {
    currentYear: number
    schoolSubjects: ApplicationSchoolSubject[] | null | undefined
}): ApplicationQuestion[] {
    const text = { required: true, noLeadingWhitespace: true }
    const score = { required: true, number: true, min: 0, max: 20 }
    return [
        {
            kind: "text",
            label: "Indique tes coordonnées",
            inputs: [
                { name: "firstname", placeholder: "Prénom", rules: text },
                { name: "lastname", placeholder: "Nom", rules: text },
                {
                    name: "mobile",
                    placeholder: "Téléphone portable",
                    keyboard: "phone-pad",
                    rules: { ...text, phone: true },
                },
            ],
        },
        {
            kind: "single",
            name: "graduation_year",
            label: "En quelle année as-tu obtenu ton bac ?",
            options: graduationYears(args.currentYear),
            layout: "chips",
            toggle: "abroadBac",
        },
        {
            kind: "single",
            name: "sector",
            label: "Peux-tu préciser la filière de ton bac ?",
            options: values(["Générale", "Technologique"]),
        },
        {
            kind: "multiple",
            name: "specialty_school_subjects",
            label: "Quelles étaient tes matières de spécialité au bac ?",
            options: (args.schoolSubjects ?? [])
                .filter((subject) => !!subject?.is_specialty)
                .map((subject) => ({ value: subject.id, label: subject.name ?? "" })),
        },
        { kind: "languages", name: "foreign_languages", label: "Quelles sont les langues étrangères que tu connais ?" },
        {
            kind: "multiple",
            name: "relevant_subjects",
            label: "Tu te sens plutôt littéraire, scientifique ou les deux ?",
            options: subjectPriorities as ApplicationOption[],
        },
        {
            kind: "single",
            name: "mention",
            label: "Quelle mention as-tu obtenue au bac ?",
            options: values(["Très bien ou plus", "Bien", "Assez bien", "Pas de mention"]),
        },
        {
            kind: "text",
            label: "En première, quelle note as-tu obtenu à l'écrit du bac de français ?",
            required: true,
            inputs: [
                {
                    name: "writing_french_score",
                    placeholder: "Note à l'écrit",
                    keyboard: "decimal-pad",
                    rules: score,
                },
                {
                    name: "oral_french_score",
                    label: "Et à l'oral ?",
                    placeholder: "Note à l'oral",
                    keyboard: "decimal-pad",
                    rules: score,
                },
            ],
        },
        {
            kind: "single",
            name: "study_level",
            label: "Quel est ton niveau d'étude déjà validé ?",
            options: values(["BAC", "BAC+1", "BAC+2", "BAC+3", "BAC+4", "BAC+5", "BAC+6 ou plus"]),
            layout: "chips",
        },
        {
            kind: "text",
            label: "Quel est ton établissement d'étude ?",
            required: true,
            inputs: [
                { name: "study_institution", placeholder: "Université Paris-Saclay", rules: text },
                {
                    name: "study_field",
                    label: "Pour quelle formation ?",
                    placeholder: "Licence de commerce international",
                    rules: text,
                },
            ],
        },
        {
            kind: "text",
            label: "Si tu es déjà auto-entrepreneur, indique ton SIRET",
            toggle: "autoEntrepreneur",
            note: SIRET_NOTE,
            inputs: [
                {
                    name: "num_SIRET",
                    placeholder: "Numéro SIRET (14 chiffres)",
                    rules: { noLeadingWhitespace: true },
                    hiddenUnless: "autoEntrepreneur",
                },
            ],
        },
    ]
}

const notSupport = (answers: ApplicationValues) => answers.mot_support !== "Oui"

/** La motivation écrite, étape 2 (`motivationQuestions`). */
export const MOTIVATION_QUESTIONS: ApplicationQuestion[] = [
    {
        kind: "single",
        name: "mot_support",
        label: "As-tu déjà donné des cours de soutien ?",
        options: values(["Oui", "Non"]),
    },
    {
        kind: "multiple",
        name: "mot_scope",
        label: "Dans quel cadre ?",
        options: [
            ...values([
                "Cours en présentiel avec une agence",
                "Cours en distanciel avec une agence",
                "Au sein de ma famille",
            ]),
            { value: "autre", label: "Autre" },
        ],
        masked: notSupport,
    },
    {
        kind: "single",
        name: "mot_duration",
        label: "Pendant combien de temps ?",
        options: [
            { value: "moins_6_mois", label: "Moins de 6 mois" },
            { value: "moins_1_an", label: "Moins d'1 an" },
            { value: "plus_1_an", label: "Plus d'1 an" },
        ],
        masked: notSupport,
    },
    {
        kind: "multiple",
        name: "mot_classes",
        label: "Pour quelles classes ?",
        options: values([
            "Essentiellement primaire",
            "Essentiellement collégien",
            "Essentiellement lycéen",
            "Essentiellement post bac",
        ]),
        masked: notSupport,
    },
    {
        kind: "single",
        name: "mot_tutoring_hrs",
        label: "Indique moi le nombre d'heures de cours que tu es prêt à donner chaque semaine de manière régulière ?",
        options: [
            {
                value: "2h à 4h",
                label: "Entre 2h et 4h par semaine",
                detail: "(rémunération espérée : entre 160 et 300 €/mois)",
            },
            {
                value: "4h à 6h",
                label: "Entre 4h et 6h par semaine",
                detail: "(rémunération espérée : entre 300 et 450 €/mois)",
            },
            {
                value: "6h à 8h",
                label: "Entre 6h et 8h par semaine",
                detail: "(rémunération espérée : entre 450 et 600 €/mois)",
            },
            {
                value: "Plus de 8h",
                label: "Plus de 8h par semaine",
                detail: "(rémunération espérée : 600 €/mois et plus)",
            },
        ],
    },
]

/** La pédagogie écrite, étape 4 (`pedagogyQuestions`). */
export const PEDAGOGY_QUESTIONS: ApplicationQuestion[] = [
    {
        kind: "single",
        name: "edu_grade_preference",
        label: "As tu une préférence pour accompagner des jeunes élèves ou des élèves plus avancés ?",
        options: values([
            "Je préfère accompagner des élèves du CM1 à la 5ème",
            "Je préfère accompagner des élèves de la 4ème à la 2nde",
            "Je préfère accompagner des élèves de la 1ère et plus",
            "Je n'ai pas de préférence",
        ]),
        precision: {
            placeholder: "Si tu le souhaites, explique moi ton choix, ça m'aidera à mieux te connaître.",
        },
    },
    {
        kind: "multiple",
        name: "edu_competencies",
        label: "Parmi les compétences pédagogiques suivantes, peux-tu m’en citer 2 que tu penses maîtriser ?",
        options: values([
            "Expliquer clairement des notions de mon domaine",
            "Redonner confiance à un élève",
            "Nouer un lien fort avec un élève",
            "Remotiver un élève",
            "Aider un élève à s'organiser",
            "Faire preuve de patience avec un élève en grande difficulté",
        ]),
        exactly: 2,
    },
]

// ─── Une question à l'écran ───────────────────────────────────────────────────────────────────

export type ApplicationToggles = Record<ApplicationToggleKey, boolean>

/** Les champs visibles d'une question écrite (le SIRET suit son interrupteur). */
export function visibleInputs(
    question: Extract<ApplicationQuestion, { kind: "text" }>,
    toggles: ApplicationToggles
): ApplicationInput[] {
    return question.inputs.filter((input) => input.hiddenUnless == null || toggles[input.hiddenUnless])
}

/** Une réponse en liste (choix multiple, langues), vide sinon. */
export function listOf<T = string | number>(value: unknown): T[] {
    return Array.isArray(value) ? (value as T[]) : []
}

/**
 * Le bouton du pied est-il là ? `hideNextButton` de `FormStepper` : une question à choix l'attend
 * tant que rien n'est choisi (et 2 compétences pour la pédagogie) ; une question écrite marquée
 * « requise » tant qu'un champ est vide ; les langues ont toujours leur « Continuer » ; un choix
 * avec précision a son « Suivant » une fois choisi. Un choix unique sans précision passe aussi à la
 * suivante d'un geste, sauf à la dernière question (`autoAdvances`).
 */
export function hasFooterAction(args: {
    question: ApplicationQuestion
    values: ApplicationValues
    toggles: ApplicationToggles
}): boolean {
    const { question, values: answers, toggles } = args
    switch (question.kind) {
        case "single":
            return answers[question.name] != null
        case "multiple": {
            const count = listOf(answers[question.name]).length
            return question.exactly != null ? count >= question.exactly : count > 0
        }
        case "text":
            // `isStepDirty` de `FormStepper`, lu comme « écrit » : un champ restauré compte pour écrit
            // (l'ancien le tenait pour vierge et cachait le bouton jusqu'à une frappe, divergence voulue).
            return !question.required || visibleInputs(question, toggles).every((input) => !!answers[input.name])
        case "languages":
            return true
    }
}

/** Choisir passe à la question suivante (`select.single` de `FormStep`), sauf à la dernière et sous une précision. */
export function autoAdvances(question: ApplicationQuestion, isLast: boolean): boolean {
    return question.kind === "single" && question.precision == null && !isLast
}

/** Les options d'un choix multiple, refusées une fois le maximum atteint. */
export function optionDisabled(question: ApplicationQuestion, answers: ApplicationValues, value: string | number) {
    if (question.kind !== "multiple" || question.exactly == null) return false
    const chosen = listOf(answers[question.name])
    return chosen.length >= question.exactly && !chosen.includes(value)
}

/** Les erreurs d'une question écrite, par champ, avant de passer à la suivante (`trigger()`). */
export function questionErrors(
    question: ApplicationQuestion,
    answers: ApplicationValues,
    toggles: ApplicationToggles
): Record<string, string> {
    if (question.kind === "languages") {
        const missing = languagesWithoutLevel(answers.foreign_languages)
        return missing.length > 0 ? { foreign_languages: LANGUAGE_LEVEL_MESSAGE } : {}
    }
    if (question.kind !== "text") return {}
    const errors: Record<string, string> = {}
    for (const input of visibleInputs(question, toggles)) {
        const message = fieldError(answers[input.name] as string | undefined, input.rules)
        if (message != null) errors[input.name] = message
    }
    return errors
}

// ─── Les langues (ForeignLanguageStep, LanguageSelector) ──────────────────────────────────────

/** Les niveaux de `LanguageSelector` : 1, 2, 3. */
const LEVEL_TO_PROFICIENCY: Record<UITutorLanguageLevel, number> = { conversational: 1, fluent: 2, native: 3 }

export function proficiencyOf(level: UITutorLanguageLevel): number {
    return LEVEL_TO_PROFICIENCY[level]
}

function levelOf(proficiency: number | null | undefined): UITutorLanguageLevel | null {
    const found = (Object.keys(LEVEL_TO_PROFICIENCY) as UITutorLanguageLevel[]).find(
        (level) => LEVEL_TO_PROFICIENCY[level] === proficiency
    )
    return found ?? null
}

export function languagesWithoutLevel(value: unknown): ApplicationLanguageAnswer[] {
    return listOf<ApplicationLanguageAnswer>(value).filter(
        (lang) => lang.proficiency === undefined || lang.proficiency === null
    )
}

/** Les langues proposées : les matières de catégorie `language`. */
export function availableLanguages(schoolSubjects: ApplicationSchoolSubject[] | null | undefined) {
    return (schoolSubjects ?? [])
        .filter((subject) => subject?.category === "language")
        .map((subject) => ({ id: String(subject.id), name: subject.name ?? "" }))
}

/** Les langues déclarées, à l'écran ; une langue inconnue des matières n'est pas montrée (comme l'ancien). */
export function toUILanguages(value: unknown, available: { id: string; name: string }[]): UITutorLanguage[] {
    return listOf<ApplicationLanguageAnswer>(value)
        .map((lang) => {
            const known = available.find((language) => language.id === String(lang.id))
            return known ? { id: known.id, name: known.name, level: levelOf(lang.proficiency) } : null
        })
        .filter((lang): lang is UITutorLanguage => lang != null)
}

// ─── Ce qui part au serveur ───────────────────────────────────────────────────────────────────

/** `ApplicationPersonalInfos.onSubmit` : le corps de `PUT /api/v1/personal-infos`. */
export function toPersonalInfosPayload(answers: ApplicationValues, toggles: ApplicationToggles) {
    return {
        firstname: answers.firstname,
        lastname: answers.lastname,
        mobile: answers.mobile,
        study_institution: answers.study_institution,
        bac_infos: {
            graduation_year: answers.graduation_year,
            abroad_bac: toggles.abroadBac,
            sector: answers.sector,
            mention: answers.mention,
            writing_french_score: answers.writing_french_score,
            oral_french_score: answers.oral_french_score,
        },
        relevant_subjects: answers.relevant_subjects,
        study_level: answers.study_level,
        study_field: answers.study_field,
        num_SIRET: toggles.autoEntrepreneur ? answers.num_SIRET : null,
        specialty_school_subjects: answers.specialty_school_subjects,
        foreign_languages: answers.foreign_languages,
    }
}

/**
 * `InterviewScreen.onSubmit` : le corps de `POST /api/v1/app-questions/submit-answers`. Chaque
 * réponse donnée part, même celle d'une question masquée depuis (« Oui » puis « Non » : l'ancien
 * formulaire gardait la valeur), un choix multiple en liste, un choix unique en `{ text }`, avec la
 * précision libre quand il y en a une. Une question masquée qu'on a seulement vue (« Oui », la
 * question suivante, retour, « Non ») ne part pas : l'ancien formulaire l'enregistrait vide
 * (`{ text: null }`), une réponse que personne ne lit. Divergence voulue.
 */
export function toTestAnswersPayload(answers: ApplicationValues, precisions: Record<string, string>) {
    return {
        application_question_answers: Object.keys(answers)
            .filter((key) => answers[key] != null)
            .map((key) => {
                const value = answers[key]
                const precision = precisions[key] ? { precision: precisions[key] } : {}
                return { question_slug: key, answer: Array.isArray(value) ? value : { text: value, ...precision } }
            }),
    }
}
